import { useContext, useMemo } from 'react';

import { getCanvasColor } from '../../core/components/canvas/utils/canvas-color.js';
import type {
  ColorPalette,
  CustomColorPalette,
} from '../../core/types/color-palette.js';
import { buildCategoricalColorScale } from '../../core/utils/build-categorical-color-scale.js';
import { SankeyColorPaletteContext } from '../context/SankeyColorPalette.context.js';
import { SankeyDataContext } from '../context/SankeyData.context.js';
import type { SankeyGraphNode } from '../types/sankey-internal.js';

function buildPerNodeColorTokenMap(
  nodes: SankeyGraphNode[],
  colorPalette: ColorPalette | CustomColorPalette,
): Map<string, string> {
  const ids = nodes.map((n) => n.id);
  const scale = buildCategoricalColorScale(colorPalette, ids);
  const tokenMap = new Map<string, string>();
  for (const node of nodes) {
    tokenMap.set(node.id, scale(node.id));
  }
  return tokenMap;
}

// Prevents Array.from from allocating unsafely when a malformed or adversarial
// column index is present in the data.
const MAX_COLUMN_DOMAIN_SIZE = 512;

function buildColumnColorTokenMap(
  nodes: SankeyGraphNode[],
  colorPalette: ColorPalette | CustomColorPalette,
): Map<string, string> {
  const uniqueColumns = [
    ...new Set(
      nodes.map((n) => n.column).filter((c): c is number => c !== undefined),
    ),
  ].sort((a, b) => a - b);

  const maxColumn = uniqueColumns[uniqueColumns.length - 1];
  if (!Number.isFinite(maxColumn) || maxColumn < 0) {
    return new Map();
  }
  const fullDomain = Array.from(
    { length: Math.min(maxColumn + 1, MAX_COLUMN_DOMAIN_SIZE) },
    (_, i) => String(i),
  );
  const scale = buildCategoricalColorScale(colorPalette, fullDomain);

  const tokenMap = new Map<string, string>();
  for (const node of nodes) {
    const key = node.column !== undefined ? String(node.column) : '0';
    tokenMap.set(node.id, scale(key));
  }
  return tokenMap;
}

/**
 * Returns a map from node id to resolved hex color.
 *
 * When nodes carry an explicit column assignment (from `columnAccessor`), all
 * nodes in the same column receive the same categorical palette color so that
 * columns are visually grouped. Without column assignments each node receives
 * its own palette color in document order.
 */
export function useNodeColors(): Map<string, string> {
  const { nodes } = useContext(SankeyDataContext);
  const colorPalette = useContext(SankeyColorPaletteContext);

  // Only the id -> token mapping is memoized: it's a plain categorical scale
  // and doesn't depend on the active theme. Resolving each token to a hex
  // color via `getCanvasColor` happens on every call (as elsewhere in the
  // charts codebase, e.g. BarLabelOverlay, draw-shape.ts) so it always reads
  // the current `[data-theme]` from the DOM instead of baking in whatever
  // theme happened to be applied the first time this map was built.
  const tokenMap = useMemo(() => {
    const hasColumnAssignment = nodes.some((n) => n.column !== undefined);
    return hasColumnAssignment
      ? buildColumnColorTokenMap(nodes, colorPalette)
      : buildPerNodeColorTokenMap(nodes, colorPalette);
  }, [nodes, colorPalette]);

  const colorMap = new Map<string, string>();
  for (const [id, token] of tokenMap) {
    colorMap.set(id, getCanvasColor(token));
  }
  return colorMap;
}
